import { breadcrumbSlot } from "@redotech/arbiter/breadcrumb";
import { Flex } from "@redotech/arbiter/flex";
import { t } from "@redotech/redo-i18n";
import { PermissionedRouteObject } from "@redotech/redo-merchant-app-common/routing/protected-routes";
import { settingsPagePermission } from "@redotech/redo-merchant-app-common/routing/settings-page-permission";
import { memo } from "react";
import { Navigate, Outlet, useLocation } from "react-router-dom";
import { TranslatedBreadcrumbLabel } from "../../../../return/returns-breadcrumb-label";
import { SettingsTabRedirect } from "../settings-tab-redirect";

const orderEditingSettingsBreadcrumb = breadcrumbSlot();

const WarehouseDashboardRedirect = memo(function WarehouseDashboardRedirect() {
  const { hash, pathname, search } = useLocation();
  const legacyPath = "/settings/oms/warehouse-dashboard";
  if (!pathname.includes(legacyPath)) {
    return null;
  }
  const params = new URLSearchParams(search);
  params.set("tab", "dashboard");
  return (
    <Navigate
      replace
      to={`${pathname.replace(legacyPath, "/settings/oms/scanning-warehouse")}?${params}${hash}`}
    />
  );
});

export const fulfillmentSettingsRoutes: PermissionedRouteObject[] = [
  {
    handle: {
      get breadcrumb() {
        return t("Order management");
      },
      breadcrumbNavigate: false,
      permission: settingsPagePermission(
        "order-fulfillment/order-management",
        "order-fulfillment/order-editing",
      ),
    },
    path: "order-management",
    children: [
      {
        index: true,
        lazy: async () => {
          const { OrderManagementSettingsPage } =
            await import("../../../../setting/order-management/order-management-settings");
          return {
            Component: function OrderManagementSettingsRoute() {
              const { pathname, search } = useLocation();
              const params = new URLSearchParams(search);
              if (params.get("section") === "order-editing") {
                params.delete("section");
                return (
                  <Navigate
                    replace
                    to={`${pathname}/order-editing${params.size ? `?${params}` : ""}`}
                  />
                );
              }
              return <OrderManagementSettingsPage />;
            },
          };
        },
      },
      {
        handle: { breadcrumb: orderEditingSettingsBreadcrumb },
        path: "order-editing",
        lazy: async () => {
          const { OrderEditingSettingsPage } =
            await import("../../../../setting/order-editing/order-editing-settings");
          return {
            element: (
              <>
                <TranslatedBreadcrumbLabel
                  breadcrumb={orderEditingSettingsBreadcrumb}
                  getLabel={(t) =>
                    t("Order editing", {
                      context: "merchant settings label for order editing",
                    })
                  }
                />
                <OrderEditingSettingsPage />
              </>
            ),
          };
        },
      },
    ],
  },
  {
    handle: {
      get breadcrumb() {
        return t("Order fulfillment");
      },
      breadcrumbNavigate: false,
      permission: settingsPagePermission(
        "order-fulfillment/general",
        "order-fulfillment/auto-merge",
        "order-fulfillment/automations",
        "order-fulfillment/custom-imports",
        "order-fulfillment/international",
        "order-fulfillment/packages",
        "order-fulfillment/presets",
        "order-fulfillment/printing",
        "order-fulfillment/packing-slips",
        "order-fulfillment/reports",
        "order-fulfillment/scanning-warehouse",
        "order-fulfillment/warehouse-dashboard",
        "order-fulfillment/security",
        "order-fulfillment/warehouse-layouts",
        "order-fulfillment/work-order-flows",
      ),
    },
    path: "oms",
    children: [
      {
        handle: {
          get breadcrumb() {
            return t("Order preparation");
          },
          permission: settingsPagePermission("order-fulfillment/general"),
        },
        path: "general",
        element: (
          <SettingsTabRedirect
            from="/settings/oms/general"
            section="order-preparation"
            to="/settings/order-management"
          />
        ),
      },
      {
        handle: {
          get breadcrumb() {
            return t("Merge & split rules");
          },
          permission: settingsPagePermission("order-fulfillment/auto-merge"),
        },
        path: "automerge-shipments",
        element: (
          <SettingsTabRedirect
            from="/settings/oms/automerge-shipments"
            section="merging"
            sectionByLegacyTab={{ merging: "merging", splitting: "splitting" }}
            to="/settings/order-management"
          />
        ),
      },
      {
        handle: {
          get breadcrumb() {
            return t("Automations", {
              context:
                "merchant settings label for order fulfillment automations",
            });
          },
          permission: settingsPagePermission("order-fulfillment/automations"),
        },
        path: "automations",
        children: [
          {
            index: true,
            lazy: async () => {
              const { AutomationsPage: Automations } =
                await import("@redotech/redo-merchant-fulfillment-app/setting/automations/automations");
              return { Component: Automations };
            },
          },
          {
            handle: {
              get breadcrumb() {
                return t("Automation Builder");
              },
            },
            path: ":automationId",

            async lazy() {
              const { OmsAutomationBuilder } =
                await import("../../../../setting/oms/automations/automation-builder");
              return { Component: OmsAutomationBuilder };
            },
          },
        ],
      },
      {
        handle: {
          get breadcrumb() {
            return t("Custom imports", {
              context:
                "merchant settings label for custom order import schemas",
            });
          },
          permission: settingsPagePermission(
            "order-fulfillment/custom-imports",
          ),
        },
        path: "custom-import-schemas",
        element: (
          <SettingsTabRedirect
            defaultGroup="fulfillment.custom_imports"
            from="/settings/oms/custom-import-schemas"
            section="presets-shortcuts"
            to="/settings/order-management"
          />
        ),
      },
      {
        handle: {
          get breadcrumb() {
            return t("International", {
              context:
                "merchant settings label for international order fulfillment",
            });
          },
          permission: settingsPagePermission("order-fulfillment/international"),
        },
        path: "international",
        element: (
          <SettingsTabRedirect
            from="/settings/oms/international"
            section="international"
            to="/settings/order-management"
          />
        ),
      },
      {
        handle: {
          get breadcrumb() {
            return t("Packages", {
              context: "merchant settings label for fulfillment packages",
            });
          },
          permission: settingsPagePermission("order-fulfillment/packages"),
        },
        path: "packages",
        element: (
          <SettingsTabRedirect
            defaultGroup="fulfillment.packages"
            from="/settings/oms/packages"
            section="order-preparation"
            to="/settings/order-management"
          />
        ),
      },
      {
        handle: {
          get breadcrumb() {
            return t("Presets", {
              context: "merchant settings label for order fulfillment presets",
            });
          },
          permission: settingsPagePermission("order-fulfillment/presets"),
        },
        path: "presets",
        element: (
          <SettingsTabRedirect
            from="/settings/oms/presets"
            section="presets-shortcuts"
            to="/settings/order-management"
          />
        ),
      },
      {
        handle: {
          get breadcrumb() {
            return t("Printing", {
              context: "merchant settings label for order fulfillment printing",
            });
          },
          permission: settingsPagePermission(
            "order-fulfillment/printing",
            "order-fulfillment/packing-slips",
          ),
        },
        path: "printing",
        element: <Outlet />,
        children: [
          {
            index: true,
            element: (
              <SettingsTabRedirect
                from="/settings/oms/printing"
                section="printing"
                to="/settings/order-management"
              />
            ),
          },
          {
            handle: {
              get breadcrumb() {
                return t("Packing slips");
              },
            },
            path: "packing-slips",
            children: [
              {
                index: true,
                async lazy() {
                  const { DocumentsMovedNotice } =
                    // redont-allow-dynamic-import: route-level code splitting for the carved fulfillment app
                    await import("@redotech/redo-merchant-fulfillment-app/documents/documents-moved-notice");
                  return {
                    element: (
                      <Flex
                        align="center"
                        dir="column"
                        gap="lg"
                        p="4xl"
                        w="full"
                      >
                        <Flex dir="column" gap="xl" maxW="xl" w="full">
                          <DocumentsMovedNotice destination="packing-slips" />
                        </Flex>
                      </Flex>
                    ),
                  };
                },
              },
              {
                handle: {
                  get breadcrumb() {
                    return t("Packing slip builder");
                  },
                },
                path: ":packingSlipId",
                async lazy() {
                  const { PackingSlipBuilder } =
                    // redont-allow-dynamic-import: route-level code splitting for the carved fulfillment app
                    await import("@redotech/redo-merchant-fulfillment-app/packing-slips/packing-slip-builder");
                  return { Component: PackingSlipBuilder };
                },
              },
            ],
          },
          {
            handle: {
              get breadcrumb() {
                return t("Shipping label content");
              },
            },
            path: "shipping-labels",
            children: [
              {
                index: true,
                async lazy() {
                  const { ShippingLabelDesignerPage } =
                    // redont-allow-dynamic-import: route-level code splitting for the carved fulfillment app
                    await import("@redotech/redo-merchant-fulfillment-app/setting/printing-document-designers");
                  return { Component: ShippingLabelDesignerPage };
                },
              },
              {
                path: ":shippingLabelTemplateId",
                async lazy() {
                  const { ShippingLabelBuilderLegacyRedirect } =
                    // redont-allow-dynamic-import: route-level code splitting for the carved fulfillment app
                    await import("@redotech/redo-merchant-fulfillment-app/shipping-labels/shipping-label-builder-legacy-redirect");
                  return { Component: ShippingLabelBuilderLegacyRedirect };
                },
              },
            ],
          },
        ],
      },
      {
        handle: {
          get breadcrumb() {
            return t("Reports", {
              context: "merchant settings label for order fulfillment reports",
            });
          },
          permission: settingsPagePermission("order-fulfillment/reports"),
        },
        path: "reports",
        lazy: async () => {
          const { ReportsMovedPage } =
            await import("../../../../setting/order-management/reports-moved-page");
          return { Component: ReportsMovedPage };
        },
      },
      {
        handle: {
          get breadcrumb() {
            return t("Warehouse");
          },
          permission: settingsPagePermission(
            "order-fulfillment/scanning-warehouse",
          ),
        },
        path: "scanning-warehouse",
        lazy: async () => {
          const { ScanningWarehouseSettingsPage } =
            await import("@redotech/redo-merchant-fulfillment-app/setting/scanning-warehouse");
          return { Component: ScanningWarehouseSettingsPage };
        },
      },
      {
        handle: {
          get breadcrumb() {
            return t("Warehouse dashboard", {
              context:
                "merchant settings label for the fulfillment warehouse dashboard",
            });
          },
          permission: settingsPagePermission(
            "order-fulfillment/warehouse-dashboard",
          ),
        },
        path: "warehouse-dashboard",
        element: <WarehouseDashboardRedirect />,
      },
      {
        handle: {
          get breadcrumb() {
            return t("Security", {
              context: "merchant settings label for fulfillment security",
            });
          },
          permission: settingsPagePermission("order-fulfillment/security"),
        },
        path: "security",
        element: (
          <SettingsTabRedirect
            from="/settings/oms/security"
            section="access-devices"
            to="/settings/order-management"
          />
        ),
      },
      {
        handle: {
          get breadcrumb() {
            return t("Warehouse builder");
          },
        },
        path: "warehouse-builder",
        lazy: async () => {
          const { WarehouseBuilderWithProvider } =
            await import("@redotech/redo-merchant-fulfillment-app/wms/warehouse-builder/warehouse-builder");
          return { Component: WarehouseBuilderWithProvider };
        },
      },
      {
        handle: {
          get breadcrumb() {
            return t("Warehouse layouts", {
              context:
                "merchant settings label for fulfillment warehouse layouts",
            });
          },
          permission: settingsPagePermission(
            "order-fulfillment/warehouse-layouts",
          ),
        },
        path: "warehouse-layouts",
        lazy: async () => {
          const { WarehouseLayoutsPage } =
            await import("@redotech/redo-merchant-fulfillment-app/setting/warehouse-layouts");
          return { Component: WarehouseLayoutsPage };
        },
      },
      {
        handle: {
          get breadcrumb() {
            return t("Work order flows", {
              context:
                "merchant settings label for fulfillment work order flows",
            });
          },
          permission: settingsPagePermission(
            "order-fulfillment/work-order-flows",
          ),
        },
        path: "work-order-flows",
        children: [
          {
            index: true,
            lazy: async () => {
              const { WorkOrderFlowsPage } =
                await import("@redotech/redo-merchant-fulfillment-app/setting/work-order-flows/work-order-flows");
              return { Component: WorkOrderFlowsPage };
            },
          },
          {
            handle: {
              get breadcrumb() {
                return t("Flow Builder", {
                  context:
                    "merchant settings breadcrumb for the fulfillment work order flow builder",
                });
              },
            },
            path: ":flowId",
            async lazy() {
              const { WorkOrderFlowBuilder } =
                await import("../../../../setting/oms/work-order-flows/work-order-flow-builder");
              return { Component: WorkOrderFlowBuilder };
            },
          },
        ],
      },
    ],
  },
];
